import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Form, Formik, FormikProps } from 'formik';

import { DateInput, Input, SingleSelect } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import {
  Breadcrumb,
  Button,
  Label,
  LABEL_TYPE,
  Spinner,
  Textarea,
  ToggleSwitch
} from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { TOGGLE_THEMES } from '@/components/generic/ToggleSwitch/toggle-switch-config';
import {
  CEREMONIES_LIMIT_EXCEEDED_ERROR_CODE,
  dateAndHoursFormat,
  dateAndHoursFormatRequest,
  PAGE_SIZE,
  TIME_LIMIT_EXCEEDED_ERROR_CODE
} from '@/constants';
import { AuthContext } from '@/contexts';
import { CustomAttributeType } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getPermissionsForOrganization } from '@/helpers';
import { addDays, formatDate, formatDateWithTranslation } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { dynamicValidationSchema } from '@/pages/Events//Validation';
import { CustomAttributeField } from '@/pages/Events/CustomAttributeField';
import { Events as EventIcon } from '@/resources';
import {
  useCreateEventMutation,
  useGetAllDevices,
  useGetAllEventTypes,
  useGetAllOrganizations,
  useGetCustomAttributeTemplatesByEventType,
  useGetEventByIdQuery,
  useGetOrganizationByIdSimplifiedQuery,
  useUpdateEventMutation,
  useUpdateStreamEvent
} from '@/services';
import { useGetAllCeremonyTemplates } from '@/services/ceremonyTemplates.service';
import {
  CeremonyTemplate,
  CreateEditEventPayload,
  CustomAttributeTemplate,
  CustomAttributeValue,
  EventType,
  Organization,
  SelectOptionProps
} from '@/types';

const LIVE_STREAM_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric'
};

const LIVE_STREAM_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
};

const FREE_UPLOAD_ZONE_OPTION_VALUE = 'freeUploadZone';

export const CreateEditEvent = () => {
  const { eventId } = useParams();
  const [t] = useTranslation('common');
  const isEditPage = !!eventId;
  const queryClient = useQueryClient();
  const { currentUserData, organizationPermissions } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const numberOfUsersOrganizations = currentUserData && currentUserData?.organizations?.length;
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<number>();
  const navigate = useNavigate();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(0);
  const [selectedEventType, setSelectedEventType] = useState<EventType>({ id: -1, name: '' });
  const [initialValues, setInitialValues] = useState<CreateEditEventPayload>({
    id: -1,
    name: '',
    description: '',
    enabled: false,
    startDateAndTime: new Date(),
    contentDeadline: new Date(),
    organizationId: 0,
    ceremonyTemplateId: null,
    customAttributeValues: [],
    deviceId: undefined
  });
  const [selectedCeremonyTemplate, setSelectedCeremonyTemplate] =
    useState<SelectOptionProps | null>(null);
  const [toggleSwitch, setToggleSwitch] = useState<boolean>(false);
  const [isFreeUploadZoneToggle, setIsFreeUploadZoneToggle] = useState<boolean>(false);
  const formikRef = useRef<FormikProps<CreateEditEventPayload>>(null);

  const { data: selectedOrganization, isFetching: isFetchingSelectedOrganization } =
    useGetOrganizationByIdSimplifiedQuery(selectedOrganizationId);
  const hasCeremonySetupAccess = getPermissionsForOrganization(
    organizationPermissions,
    selectedOrganization?.data
  ).includes(ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP);
  const canLoadTemplates = hasCeremonySetupAccess && !isFetchingSelectedOrganization;

  const {
    data: allOrganizations,
    hasNextPage,
    fetchNextPage
  } = useGetAllOrganizations(PAGE_SIZE, searchValue, undefined, numberOfUsersOrganizations === 0);

  const { data: eventTypes } = useGetAllEventTypes(undefined, selectedOrganizationId);
  const { data: customAttributeTemplates } = useGetCustomAttributeTemplatesByEventType(
    selectedEventType.id,
    CustomAttributeType.CREATE_EDIT
  );

  const { mutate: createEvent } = useCreateEventMutation(queryClient, {
    onSuccess: () => {
      navigate('/events');
      showToast(ToastType.SUCCESS, t('eventCreated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      const backendError = error.response?.data?.ErrorMessages?.[0];
      const message = backendError?.DisplayMessage ?? t('eventCreatedError');
      const isLimitExceeded =
        backendError?.Code === CEREMONIES_LIMIT_EXCEEDED_ERROR_CODE ||
        backendError?.Code === TIME_LIMIT_EXCEEDED_ERROR_CODE;

      if (isLimitExceeded) {
        showToast(ToastType.WARNING, {
          text: message,
          autoClose: false
        });
      } else {
        showToast(ToastType.ERROR, message);
      }

      setIsSubmitting(false);
    }
  });

  const { mutate: updateEvent } = useUpdateEventMutation(queryClient, {
    onSuccess: () => {
      navigate(`/events/${eventId}`);
      showToast(ToastType.SUCCESS, t('eventUpdated'));
      setIsSubmitting(false);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: updateStreamEvent } = useUpdateStreamEvent(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('streamEventSuccessfullyUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { data: eventData, isLoading } = useGetEventByIdQuery(Number(eventId), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const { data: eventTypeForEdit } = useGetAllEventTypes(
    eventData?.data.customAttributeValues[0]?.customAttributeTemplateId ?? -1,
    undefined,
    isEditPage && !!eventData?.data.customAttributeValues[0]?.customAttributeTemplateId
  );

  const { data: allDevices, isLoading: isLoadingDevices } = useGetAllDevices(
    PAGE_SIZE,
    '',
    undefined,
    undefined,
    isEditPage,
    eventData?.data.organization.id
  );

  useEffect(() => {
    if (currentUserData?.organizations?.length === 1) {
      setSelectedOrganizationId(currentUserData?.organizations[0].id as number);
    }
  }, []);

  useEffect(() => {
    if (eventTypeForEdit?.data?.items[0]) {
      setSelectedEventType(eventTypeForEdit?.data?.items[0]);
    }
  }, [eventTypeForEdit]);

  const {
    data: allTemplates,
    isLoading: isLoadingTemplates,
    refetch: refetchAllTemplates
  } = useGetAllCeremonyTemplates(0, selectedOrganizationId, canLoadTemplates);
  const ceremonyTemplates: CeremonyTemplate[] = canLoadTemplates
    ? (allTemplates?.pages.flatMap((page) => page.data?.items) ?? [])
    : [];

  const setSpecificTimeForTomorrow = (date: Date, hours: number, minutes: number): Date => {
    if (!date) {
      date = new Date();
    }
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(hours, minutes, 0, 0);
    return newDate;
  };

  useEffect(() => {
    let newInitialValues: CreateEditEventPayload = {
      id: eventData?.data.id,
      name: eventData?.data.name || '',
      description: eventData?.data.description || '',
      enabled: !!eventData?.data.enabled,
      startDateAndTime: setSpecificTimeForTomorrow(new Date(), 10, 0),
      contentDeadline: setSpecificTimeForTomorrow(new Date(), 9, 0),
      organizationId: numberOfUsersOrganizations === 1 ? currentUserData?.organizations[0]?.id : 0,
      ceremonyTemplateId: null,
      customAttributeValues: [],
      deviceId: eventData?.data.device?.id
    };

    if (isEditPage && eventData) {
      newInitialValues = {
        ...newInitialValues,
        deviceId: eventData?.data.device?.id,
        organizationId: eventData?.data.organization.id,
        startDateAndTime: new Date(eventData?.data.startDateAndTime),
        contentDeadline: new Date(eventData?.data.contentDeadline),
        ceremonyTemplateId: eventData?.data?.ceremonyTemplate?.id || null
      };
    }

    setInitialValues(newInitialValues);
  }, [eventData, isEditPage, numberOfUsersOrganizations, currentUserData]);

  useEffect(() => {
    if (customAttributeTemplates?.data) {
      let customAttributeValues: CustomAttributeValue[] = [];
      if (isEditPage && eventData) {
        customAttributeValues = customAttributeTemplates?.data.map(
          (at: CustomAttributeTemplate) => ({
            value:
              eventData?.data.customAttributeValues?.find(
                (cv: CustomAttributeValue) => cv.customAttributeTemplateId === at.id
              )?.value ?? '',
            id:
              eventData?.data.customAttributeValues?.find(
                (cv: CustomAttributeValue) => cv.customAttributeTemplateId === at.id
              )?.id ?? -1,
            customAttributeTemplateId: at.id,
            eventId: eventData?.data.id
          })
        );
      } else {
        customAttributeValues = customAttributeTemplates?.data.map(
          (at: CustomAttributeTemplate) => ({
            value: '',
            customAttributeTemplateId: at.id
          })
        );
      }

      if (formikRef.current?.dirty) {
        formikRef.current.setFieldValue('customAttributeValues', customAttributeValues);
      } else {
        setInitialValues((prev) => ({
          ...prev,
          customAttributeValues
        }));
      }
    }
  }, [customAttributeTemplates, eventData]);

  useEffect(() => {
    if (eventData) {
      setSelectedOrganizationId(eventData.data.organization.id);
    }

    if (isEditPage && allTemplates?.pages.flatMap((page) => page.data?.items)) {
      setSelectedCeremonyTemplate({
        value: eventData?.data?.ceremonyTemplate?.id ? eventData.data.ceremonyTemplate.id : null,
        label: eventData?.data?.ceremonyTemplate?.id
          ? eventData.data.ceremonyTemplate.name
          : t('custom')
      });
    } else {
      setSelectedCeremonyTemplate(null);
    }

    setToggleSwitch(eventData?.data?.relatedUsersCanGenerate);
    setIsFreeUploadZoneToggle(eventData?.data?.isFreeUploadZoneEnabled);
  }, [eventData, allTemplates]);

  useEffect(() => {
    if (eventTypes?.data?.items.length === 1 && !isEditPage) {
      setSelectedEventType(eventTypes?.data?.items[0]);
    }
  }, [eventTypes]);

  const getOrganizationOptionsForAdmin = () => {
    return allOrganizations?.pages[0].data?.items.map((organization: Organization) => ({
      value: organization.id,
      label: organization.name
    }));
  };

  const getOrganizationOptionsForCreator = () => {
    if ((numberOfUsersOrganizations as number) > 1) {
      return currentUserData?.organizations.map((org) => ({
        value: org.id,
        label: org.name
      }));
    }
  };

  const getOrganizationDefaultValue = (values: CreateEditEventPayload) => {
    if (isEditPage && numberOfUsersOrganizations === 0) {
      return {
        value: values.organizationId,
        label: allOrganizations?.pages[0].data?.items.find(
          (org: Organization) => org.id === values.organizationId
        )?.name
      } as SelectOptionProps;
    } else if (isEditPage && numberOfUsersOrganizations !== 1) {
      return {
        value: values.organizationId,
        label: currentUserData?.organizations.find((org) => org.id === values.organizationId)?.name
      } as SelectOptionProps;
    }
  };

  const handleSubmit = (values: CreateEditEventPayload) => {
    setIsSubmitting(true);
    const formattedStartDateAndTime = format(
      new Date(values.startDateAndTime),
      dateAndHoursFormatRequest
    );
    const payload = {
      ...values,
      startDateAndTime: formattedStartDateAndTime,
      enabled: isEditPage ? values.enabled : true,
      eventTypeId: selectedEventType.id,
      relatedUsersCanGenerate: toggleSwitch,
      isFreeUploadZoneEnabled: isEditPage
        ? isFreeUploadZoneToggle
        : selectedCeremonyTemplate?.value === FREE_UPLOAD_ZONE_OPTION_VALUE
    };

    if (isEditPage) {
      updateEvent(payload);

      const newDate = new Date(payload.startDateAndTime);
      const oldDate = new Date(eventData?.data.startDateAndTime);
      if (newDate.getTime() !== oldDate.getTime() && eventData?.data.streamEvent) {
        eventData.data.streamEvent.description = t('waitingForLiveStream', {
          date: formatDateWithTranslation(
            new Date(payload.startDateAndTime),
            t,
            LIVE_STREAM_DATE_FORMAT_OPTIONS
          ),
          time: formatDate(new Date(payload.startDateAndTime), LIVE_STREAM_TIME_FORMAT_OPTIONS)
        });

        updateStreamEvent(eventData?.data.streamEvent);
      }
    } else {
      createEvent(payload);
    }
  };

  const getAvailabilityInitialValue = (value: boolean) => {
    return {
      value: value,
      label: value ? t('enabled') : t('disabled')
    };
  };

  useEffect(() => {
    if (selectedOrganizationId !== 0 && canLoadTemplates) {
      refetchAllTemplates();
    }
  }, [selectedOrganizationId, canLoadTemplates, refetchAllTemplates]);

  const getTemplateOptions = () => {
    const templateOptions: SelectOptionProps[] = ceremonyTemplates.map(
      (ceremonyTemplate: CeremonyTemplate) => ({
        value: ceremonyTemplate.id,
        label: t(ceremonyTemplate.name)
      })
    );
    const customOption: SelectOptionProps[] = [
      {
        value: null,
        label: t('custom'),
        infoText: t('customTemplateInfo')
      }
    ];
    const freeUploadZoneOption: SelectOptionProps[] = isEditPage
      ? []
      : [
          {
            value: FREE_UPLOAD_ZONE_OPTION_VALUE,
            label: t('freeUploadZone'),
            infoText: t('freeUploadZoneInfo')
          }
        ];
    const leadingOptions = freeUploadZoneOption.concat(customOption);
    if (templateOptions.length > 0) {
      leadingOptions[leadingOptions.length - 1].hasDivider = true;
    }

    return leadingOptions.concat(templateOptions);
  };

  const getTemplateValues = (selectedTemplate: SelectOptionProps) => {
    const templateOptions: SelectOptionProps[] = ceremonyTemplates.map(
      (ceremonyTemplate: CeremonyTemplate) => ({
        value: ceremonyTemplate.id,
        label: t(ceremonyTemplate.name)
      })
    );
    const customOption: SelectOptionProps[] = [{ value: null, label: t('custom') }];
    const freeUploadZoneOption: SelectOptionProps[] = isEditPage
      ? []
      : [{ value: FREE_UPLOAD_ZONE_OPTION_VALUE, label: t('freeUploadZone') }];
    const allOptions = customOption.concat(freeUploadZoneOption, templateOptions);

    return selectedCeremonyTemplate === null
      ? null
      : (allOptions
          .map((e) => e && { value: e.value, label: t(e.label) })
          .find((et: SelectOptionProps) => et?.value === selectedTemplate?.value) ?? t('custom'));
  };

  const getEventTypeValues = () =>
    isEditPage
      ? {
          value: eventTypeForEdit?.data?.items[0].id ?? '',
          label: t(eventTypeForEdit?.data?.items[0].name) ?? ''
        }
      : (eventTypes?.data?.items
          .map((e: EventType) => ({ value: e.id, label: t(e.name) }))
          .find((et: SelectOptionProps) => et?.value === selectedEventType.id) ?? null);

  const getPageTitle = (isSubmitButton?: boolean) => {
    const isEditLabel = isEditPage && !isSubmitButton;
    let eventTypeId = -1;
    if (isEditPage) {
      eventTypeId = eventTypeForEdit?.data?.items[0].id;
    } else {
      eventTypeId =
        eventTypes?.data?.items.length > 1 || eventTypes?.data?.items.length === 0
          ? -1
          : eventTypes?.data?.items.map((et: EventType) => et.id)[0];
    }
    switch (eventTypeId) {
      case 1:
        return isEditLabel ? t('editFuneral') : t('addFuneral');
      case 2:
        return isEditLabel ? t('editWedding') : t('addWedding');
      case 3:
        return isEditLabel ? t('editBirthday') : t('addBirthday');

      default:
        return isEditLabel ? t('editEvent') : t('addEvent');
    }
  };

  const handleToggle = () => {
    if (eventData) {
      setToggleSwitch(!toggleSwitch);
    }
  };

  const handleFreeUploadZoneToggle = () => {
    if (eventData) {
      setIsFreeUploadZoneToggle(!isFreeUploadZoneToggle);
    }
  };

  const getDeviceOptions = () => {
    const deviceOptions: SelectOptionProps[] =
      allDevices?.pages
        .flatMap((page) => page.data?.items)
        .map((device) => ({
          value: device.id,
          label: device.name,
          tag: device.status
        })) || [];
    const notSelectedOption: SelectOptionProps[] = [{ value: null, label: t('notSelected') }];
    const allOptions = notSelectedOption.concat(deviceOptions);
    return allOptions;
  };

  return (
    <div className='flex h-full justify-center overflow-y-auto'>
      {(isEditPage && isLoading) || (canLoadTemplates && isLoadingTemplates) ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='mx-3 mt-7 flex w-full flex-col md:mx-20'>
          <div className='w-full'>
            <Breadcrumb
              icon={<EventIcon className='stroke-[0.5] text-lg text-primary-blue' />}
              iconPath={'/events'}
              path={
                isEditPage
                  ? [
                      { label: eventData?.data.name, url: `/events/${eventData?.data?.id}` },
                      { label: t('editEvent') }
                    ]
                  : [{ label: t('addEvent') }]
              }
            />
          </div>
          <div className='mt-9 flex flex-col'>
            <Label text={getPageTitle()} type={LABEL_TYPE.H1} />
            <Label text={t('generalInformation')} type={LABEL_TYPE.H3} className='mb-6 mt-9' />
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              enableReinitialize
              validationSchema={dynamicValidationSchema(customAttributeTemplates?.data ?? [])}>
              {({
                touched,
                values,
                setFieldValue,
                errors,
                isValid,
                dirty,
                validateForm,
                setFieldTouched
              }) => (
                <Form className='flex flex-col justify-between'>
                  <div className='flex flex-col gap-y-6'>
                    <div className='grid gap-y-6'>
                      <div className='grid grid-cols-2 gap-x-9'>
                        {numberOfUsersOrganizations !== 1 && (
                          <SingleSelect
                            placeholder={t('selectOrganization')}
                            label={t('organization')}
                            id='organizationId'
                            containerClassName={`${isEditPage && 'opacity-50'}`}
                            isRequired
                            disabled={isEditPage}
                            options={
                              numberOfUsersOrganizations === 0
                                ? getOrganizationOptionsForAdmin()
                                : getOrganizationOptionsForCreator()
                            }
                            handleChange={(value) => {
                              setSelectedOrganizationId(value?.value as number);
                              if (values.organizationId !== (value as SelectOptionProps)?.value) {
                                setSelectedEventType({ id: -1, name: '' });
                              }
                              setFieldValue('organizationId', (value as SelectOptionProps)?.value);
                            }}
                            errorMessage={errors.organizationId}
                            isInvalid={!!errors.organizationId && touched.organizationId}
                            value={getOrganizationDefaultValue(values)}
                            handleInputChange={(inputValue: string) => setSearchValue(inputValue)}
                            fetchNextPage={fetchNextPage}
                            hasNextPage={hasNextPage}
                            isSearchable={true}
                            onBlur={() => setFieldTouched('organizationId', true)}
                          />
                        )}
                        {eventTypes?.data?.items.length > 1 && (
                          <SingleSelect
                            placeholder={t('selectEventType')}
                            label={t('eventType')}
                            id='eventType'
                            isRequired
                            containerClassName={`${isEditPage && 'opacity-50'}`}
                            disabled={isEditPage}
                            options={eventTypes?.data?.items.map((e: EventType) => ({
                              value: e.id,
                              label: t(e.name)
                            }))}
                            handleChange={(eventType) => {
                              setSelectedEventType({
                                id: (eventType?.value as number) ?? -1,
                                name: eventType?.label ?? ''
                              });
                            }}
                            value={getEventTypeValues()}
                          />
                        )}
                      </div>
                      <div className='grid grid-cols-2 gap-x-9'>
                        <Input
                          label={t('eventName')}
                          type='text'
                          id='name'
                          isRequired
                          errorMessage={touched.name ? errors.name : undefined}
                          isInvalid={!!errors.name && touched.name}
                        />
                        <DateInput
                          id='startDateAndTime'
                          label={t('dateOfCeremony')}
                          selected={values.startDateAndTime as Date}
                          onChange={(date: Date) => setFieldValue('startDateAndTime', date)}
                          errorMessage={errors.startDateAndTime as string}
                          isRequired
                          shouldPickTime
                          dateFormat={dateAndHoursFormat}
                          minDate={new Date()}
                        />
                      </div>
                    </div>
                    <div className='grid grid-flow-row grid-cols-2 gap-x-9 gap-y-6'>
                      <DateInput
                        id='contentDeadline'
                        label={t('contentDeadline')}
                        selected={values.contentDeadline}
                        onChange={(date: Date) => setFieldValue('contentDeadline', date)}
                        maxDate={addDays(values.startDateAndTime as Date, -1)}
                        errorMessage={errors.contentDeadline as string}
                        isRequired
                        shouldPickTime
                        dateFormat={dateAndHoursFormat}
                        minDate={new Date()}
                      />
                      {values.organizationId !== 0 && (
                        <SingleSelect
                          id='ceremonyTemplateId'
                          label={t('contentStructureTemplate')}
                          placeholder={t('selectContentStructureTemplate')}
                          options={getTemplateOptions()}
                          value={getTemplateValues(selectedCeremonyTemplate as SelectOptionProps)}
                          handleChange={(template) => {
                            setSelectedCeremonyTemplate(template as SelectOptionProps);
                            const templateValue = (template as SelectOptionProps)?.value;
                            setFieldValue(
                              'ceremonyTemplateId',
                              templateValue === FREE_UPLOAD_ZONE_OPTION_VALUE ? null : templateValue
                            );
                          }}
                          errorMessage={errors.ceremonyTemplateId}
                          isInvalid={!!errors.ceremonyTemplateId && touched.ceremonyTemplateId}
                        />
                      )}
                      {isEditPage ? (
                        <>
                          <SingleSelect
                            isClearable={false}
                            id='enabled'
                            label={t('availability')}
                            isRequired
                            options={[
                              { value: true, label: t('enabled') },
                              { value: false, label: t('disabled') }
                            ]}
                            handleChange={(value) =>
                              setFieldValue('enabled', (value as SelectOptionProps)?.value)
                            }
                            value={getAvailabilityInitialValue(values.enabled)}
                            errorMessage={errors.enabled}
                            isInvalid={!!errors.enabled && touched.enabled}
                          />
                          <div className='flex w-full flex-row items-center justify-between pt-7'>
                            <Label
                              className={'w-3/4 !text-sm'}
                              text={t('enableTributeVideoEdit')}
                            />
                            <ToggleSwitch
                              toggled={toggleSwitch}
                              handleToggle={handleToggle}
                              variant={TOGGLE_THEMES.LIGHT}
                            />
                          </div>
                        </>
                      ) : numberOfUsersOrganizations === 0 &&
                        (selectedOrganizationId === 0 || eventTypes?.data?.items.length <= 1) ? (
                        <div />
                      ) : (
                        <div className='hidden' />
                      )}
                      {isEditPage && (
                        <>
                          <SingleSelect
                            isClearable={false}
                            id=''
                            label={t('device')}
                            options={isLoadingDevices ? [] : getDeviceOptions()}
                            handleChange={(value) => {
                              setFieldValue('deviceId', (value as SelectOptionProps)?.value);
                            }}
                            value={getDeviceOptions().find((option) =>
                              values.deviceId
                                ? option?.value === values.deviceId
                                : option?.value === null
                            )}
                            errorMessage={String()}
                          />
                          <div className='flex w-full flex-row items-center justify-between pt-7'>
                            <Label className={'w-3/4 !text-sm'} text={t('enableFreeUploadZone')} />
                            <ToggleSwitch
                              toggled={isFreeUploadZoneToggle}
                              handleToggle={handleFreeUploadZoneToggle}
                              variant={TOGGLE_THEMES.LIGHT}
                            />
                          </div>
                        </>
                      )}

                      <div className='col-span-2'>
                        <Textarea
                          label={t('description')}
                          id='description'
                          errorMessage={errors.description}
                          isInvalid={!!errors.description && touched.description}
                        />
                      </div>
                    </div>
                  </div>
                  {selectedEventType.id !== -1 && (
                    <>
                      <div className='mb-8 mt-5 h-px w-full bg-secondary-blue-gray' />
                      <div className='flex flex-col gap-y-9'>
                        <Label text={t('personalInformation')} type={LABEL_TYPE.H3} />
                        <div className='grid grid-cols-2 gap-x-9 gap-y-2'>
                          {customAttributeTemplates?.data.map((at: CustomAttributeTemplate) => (
                            <CustomAttributeField
                              key={at.id}
                              id={at.id}
                              isRequired={at.isRequired}
                              labelKey={at.labelKey}
                              type={at.type}
                              customSymbol={at.customSymbol}
                              validationMessageKey={at.validationMessageKey}
                              placeholderKey={at.placeholderKey}
                              position={at.position}
                              errors={errors}
                              setFieldValue={setFieldValue}
                              values={values}
                              validateForm={validateForm}
                              defaultValue={
                                at.labelKey === 'nameOfDeceased'
                                  ? { id: at.id, name: values.name }
                                  : { id: at.id, name: '' }
                              }
                              isEditPage={isEditPage}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <div className='mb-12 mt-9 flex w-full justify-end'>
                    <Button
                      type='submit'
                      variant={ButtonVariant.PRIMARY}
                      disabled={
                        eventData?.data.relatedUsersCanGenerate === toggleSwitch &&
                        eventData?.data.isFreeUploadZoneEnabled === isFreeUploadZoneToggle
                          ? isSubmitting ||
                            !(isValid && dirty) ||
                            (eventTypes?.data?.items?.length > 1 && selectedEventType.id === -1)
                          : false
                      }>
                      {isEditPage ? t('saveChanges') : getPageTitle(true)}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};
